import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { createStore, applyMiddleware, combineReducers, compose } from 'redux';
import { Provider, useDispatch, useSelector } from 'react-redux';
import FullScreenLoader from './FullScreenLoader';
import thunk from 'redux-thunk';
import UAParser from 'ua-parser-js/dist/ua-parser.min';
import firebase from 'firebase';
import App from './App';
import * as serviceWorker from './serviceWorker';
import userReducer from './reducers/user';
import sentenceReducer from './reducers/sentences';
import settingsReducer from './reducers/settings';
import { setUserInfo, enterGuestMode } from './actions';
import './i18n';
import './styles/index.css';

import ReactGA from 'react-ga';
ReactGA.initialize('UA-52772108-15');
ReactGA.pageview(window.location.pathname + window.location.search);

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const store = createStore(
  combineReducers({ sentences: sentenceReducer, settings: settingsReducer, user: userReducer }),
  composeEnhancers(applyMiddleware(thunk))
);

const Wrapper = () => {
  const uid = useSelector((state) => state.user.uid);
  const isGuest = useSelector((state) => state.user.isGuest);
  const [firebaseAuthComplete, setFirebaseAuthComplete] = useState(false);
  const dispatch = useDispatch();

  React.useEffect(() => {
    const unregisterObserver = firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        // Existing users (and anyone who just logged in) keep the exact same
        // logged-in path: this is what hydrates state.user.uid on page load.
        dispatch(setUserInfo(user));
      } else {
        // No Firebase session → run as an anonymous guest (the default).
        dispatch(enterGuestMode());
      }
      setFirebaseAuthComplete(true);
    });

    return () => unregisterObserver();
  }, [dispatch]);

  // Wait until auth has resolved AND the user/guest state has been set, so we
  // never flash guest mode for a user who is about to be auto-logged-in.
  if (!firebaseAuthComplete || (!uid && !isGuest)) {
    return <FullScreenLoader />;
  }

  const { name: osName } = new UAParser().getOS();
  window.isAndroid = osName.toLowerCase() === 'android';

  // Remounting on the uid/guest transition re-runs App's settings bootstrap.
  return <App key={uid || 'guest'} />;
};

ReactDOM.render(
  <Provider store={store}>
    <Wrapper />
  </Provider>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
